import { useCallback, useEffect, useRef } from 'react';
import { usePreferencesStore } from '../store/preferencesStore';
import {
  useCollectionsStore,
  setEditingItem,
} from '../store/collectionsStore';
import { useTabsStore } from '../store/tabsStore';
import {
  useUiModalStore,
  setActiveTab,
  setAiPanelOpen,
} from '../store/uiModalStore';
import { useDevToolsStore } from '../store/devToolsStore';
import { isEditableTarget } from '../utils/keyboard';
import { matchesShortcut, isCapturingShortcut } from '../config/shortcuts';
import { runBulkDuplicate } from '../utils/sidebarBulkShortcuts';
import { isApiClientEnabled } from '../extensions/store';

/**
 * Global keyboard-shortcut listeners (AI panel, dev tools,
 * rename, new-tab, duplicate). Extracted verbatim from App — same handler
 * bodies and dep arrays. The two keydown effects are consolidated here and
 * called once (after collections/schemas/tabs handlers exist); the only change
 * vs App is that the rename listener registers at the same point as the
 * duplicate listener — both are user-input driven with no inter-effect ordering
 * dependency, so registration order is irrelevant.
 *
 * Returns `handleSidebarDuplicate` for the Sidebar's onDuplicate prop.
 *
 * @param {{
 *   handleStartSchemaRename: Function,
 *   handleNewRequestTab: Function,
 *   renameSnapshotRef: { current: any },
 *   schemaEditorRenameRef: { current: any },
 *   requestBuilderRenameRef: { current: any },
 *   duplicateRequest: Function,
 *   duplicateCollection: Function,
 *   duplicateFolder: Function,
 *   handleDuplicateSchema: Function,
 * }} deps
 */
export function useKeyboardShortcuts({
  handleStartSchemaRename,
  handleNewRequestTab,
  renameSnapshotRef,
  schemaEditorRenameRef,
  requestBuilderRenameRef,
  duplicateRequest,
  duplicateCollection,
  duplicateFolder,
  handleDuplicateSchema,
}) {
  const keyboardShortcuts = usePreferencesStore((s) => s.keyboardShortcuts);
  const selectedRequest = useCollectionsStore((s) => s.selectedRequest);
  const selectedFolder = useCollectionsStore((s) => s.selectedFolder);
  const selectedCollection = useCollectionsStore((s) => s.selectedCollection);
  const selectedSchemaEntry = useCollectionsStore((s) => s.selectedSchemaEntry);
  const schemas = useCollectionsStore((s) => s.schemas);
  const collections = useCollectionsStore((s) => s.collections);
  const activeTab = useUiModalStore((s) => s.activeTab);
  const activeTabType = useTabsStore((s) => s.activeTabType);
  const activeRequestTabId = useTabsStore((s) => s.activeRequestTabId);
  const activeCollectionTabId = useTabsStore((s) => s.activeCollectionTabId);
  const requestTabs = useTabsStore((s) => s.requestTabs);
  const collectionTabs = useTabsStore((s) => s.collectionTabs);

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      // While the Settings tab is recording a new combo, swallow nothing here —
      // let the capture input handle it and don't fire actions.
      if (isCapturingShortcut()) return;

      // The command palette (⌘K / ⌘P) has its own window-capture listener:
      // components/palette/usePaletteShortcut.js (PLAN D3).

      if (matchesShortcut(e, 'toggleAiPanel', keyboardShortcuts)) {
        e.preventDefault();
        setAiPanelOpen((open) => !open);
        return;
      }

      if (matchesShortcut(e, 'toggleDevTools', keyboardShortcuts)) {
        if (!isEditableTarget(e.target)) {
          e.preventDefault();
          useDevToolsStore.getState().toggle();
          return;
        }
      }

      if (matchesShortcut(e, 'rename', keyboardShortcuts)) {
        // WHOSE SELECTION IS THIS? Everything below reads the COLLECTIONS
        // sidebar's selection, and that selection SURVIVES a panel switch:
        // click a request, open Documents, and `selectedRequest` is still set.
        // So pressing F2 with a document highlighted renamed the stale request
        // AND called `setActiveTab('collections')`, throwing the user out of
        // the panel they were in — which is exactly the reported "F2 in
        // Documents opens Collections".
        //
        // The Documents sidebar binds rename against its own selection
        // (DocumentsSidebar.js) but registers in the BUBBLE phase while this
        // listener is in CAPTURE, so it could never win the race no matter what
        // it did. Yielding here is what lets it run.
        //
        // GIVEN UP DELIBERATELY: renaming a focused request/schema tab while a
        // non-Collections sidebar is showing. That is a fringe flow — the
        // rename UI it drives lives in the Collections sidebar anyway — and the
        // alternative is the active harm above. FTP and Vault have no rename
        // binding at all, so for them this changes nothing from a no-op to a
        // no-op.
        if (activeTab !== 'collections') return;

        e.preventDefault();

        // Schema sidebar entry selected — rename it, regardless of which sidebar
        // section is visible (schemas are now embedded in the collections sidebar).
        if (selectedSchemaEntry?.id != null) {
          const { kind, id } = selectedSchemaEntry;
          const name =
            kind === 'folder'
              ? schemas?.folders?.find((f) => String(f.id) === String(id))?.name ?? ''
              : schemas?.items?.find((it) => String(it.id) === String(id))?.name ?? '';
          handleStartSchemaRename(kind, { id, name });
          return;
        }

        if (selectedFolder?.id && !selectedCollection) {
          setActiveTab('collections');
          renameSnapshotRef.current = {
            type: 'folder',
            id: selectedFolder.id,
            previousName: selectedFolder.name,
          };
          setEditingItem({ type: 'folder', id: selectedFolder.id });
          return;
        }

        if (
          (selectedCollection && !selectedRequest) ||
          activeTabType === 'collection'
        ) {
          const colId =
            selectedCollection?.id ??
            collectionTabs.find((t) => t.id === activeCollectionTabId)?.collectionId;
          if (colId) {
            setActiveTab('collections');
            setEditingItem({ type: 'collection', id: colId });
          }
          return;
        }

        const requestId =
          selectedRequest?.id ??
          (activeTabType === 'request'
            ? requestTabs.find((t) => t.id === activeRequestTabId)?.originalId
            : null);

        if (requestId) {
          const inSidebar = collections.some((c) =>
            (c.requests || []).some((r) => String(r.id) === String(requestId))
          );
          if (inSidebar) {
            const req = collections
              .flatMap((c) => c.requests || [])
              .find((r) => String(r.id) === String(requestId));
            if (req) {
              renameSnapshotRef.current = {
                type: 'request',
                id: requestId,
                previousName: req.name,
              };
            }
            setActiveTab('collections');
            setEditingItem({ type: 'request', id: requestId });
            return;
          }
        }

        if (activeTabType === 'schema' && schemaEditorRenameRef.current) {
          schemaEditorRenameRef.current();
          return;
        }

        if (activeTabType === 'request' && requestBuilderRenameRef.current) {
          requestBuilderRenameRef.current();
        }
        return;
      }

      if (matchesShortcut(e, 'newTab', keyboardShortcuts)) {
        if (isEditableTarget(e.target)) {
          return;
        }
        // A new request tab is the API Client's; while it is switched off the
        // chord does nothing (it would add a tab nobody can see).
        if (!isApiClientEnabled()) return;
        e.preventDefault();
        handleNewRequestTab();
        return;
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown, true);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown, true);
  }, [
    selectedRequest,
    selectedFolder,
    selectedCollection,
    activeTab,
    activeTabType,
    activeRequestTabId,
    activeCollectionTabId,
    selectedSchemaEntry,
    handleStartSchemaRename,
    schemas,
    requestTabs,
    collectionTabs,
    collections,
    handleNewRequestTab,
    keyboardShortcuts,
    renameSnapshotRef,
    schemaEditorRenameRef,
    requestBuilderRenameRef,
  ]);

  const duplicateShortcutCtxRef = useRef({});

  const handleSidebarDuplicate = useCallback((type, item) => {
    const ctx = duplicateShortcutCtxRef.current;
    if (type === 'collection') {
      ctx.duplicateCollection?.(item);
    } else if (type === 'request') {
      ctx.duplicateRequest?.(item);
    } else if (type === 'folder') {
      const col = ctx.collections?.find((c) => String(c.id) === String(item.collectionId));
      if (col) {
        ctx.duplicateFolder?.(col, item);
      }
    }
  }, []);

  duplicateShortcutCtxRef.current = {
    collections,
    selectedRequest,
    selectedCollection,
    selectedFolder,
    duplicateRequest,
    duplicateCollection,
    duplicateFolder,
    duplicateSchemaEntry: handleDuplicateSchema,
  };

  useEffect(() => {
    const handleDuplicateShortcut = (e) => {
      if (isCapturingShortcut()) return;
      if (!matchesShortcut(e, 'duplicate', keyboardShortcuts)) {
        return;
      }
      // ⌘D is "split right" inside the Terminal (macOS pane shortcuts).
      if (e.target?.closest?.('.term-ext-view')) return;
      // Card 50122def: never while typing, and never while an extension panel
      // is showing — DB Manager has its own Ctrl/⌘+D (duplicate rows), and
      // this one would duplicate an unseen sidebar item at the same time.
      if (isEditableTarget(e.target)) return;
      if (String(useUiModalStore.getState().activeTab || '').endsWith('.panel')) return;
      e.preventDefault();

      // A multi-row selection in the collections tree wins over everything
      // below, which only ever knows ONE selected item.
      if (runBulkDuplicate()) return;

      const ctx = duplicateShortcutCtxRef.current;

      // Sidebar selection wins over open editor tabs (mirrors rename shortcut priority).

      const schemaEntry = useCollectionsStore.getState().selectedSchemaEntry;
      if (
        schemaEntry?.id != null &&
        (schemaEntry.kind === 'schema' || schemaEntry.kind === 'folder')
      ) {
        ctx.duplicateSchemaEntry?.(schemaEntry.kind, schemaEntry.id);
        return;
      }

      if (ctx.selectedFolder?.id && ctx.selectedFolder?.collectionId) {
        const col = (ctx.collections || []).find(
          (c) => String(c.id) === String(ctx.selectedFolder.collectionId)
        );
        const folder = col?.settings?.folders?.find(
          (f) => String(f.id) === String(ctx.selectedFolder.id)
        );
        if (col && folder && col.can_edit !== false) {
          ctx.duplicateFolder?.(col, folder);
        }
        return;
      }

      if (
        ctx.selectedCollection &&
        !ctx.selectedRequest &&
        !ctx.selectedFolder &&
        ctx.selectedCollection.can_edit !== false
      ) {
        ctx.duplicateCollection?.(ctx.selectedCollection);
        return;
      }

      if (ctx.selectedRequest?.id) {
        const req = (ctx.collections || [])
          .flatMap((c) => c.requests || [])
          .find((r) => String(r.id) === String(ctx.selectedRequest.id));
        if (req && req.can_edit !== false) {
          ctx.duplicateRequest?.(req);
        }
        return;
      }

      // No sidebar selection — fall back to the active editor tab.
      if (useTabsStore.getState().activeTabType === 'schema') {
        const schemaTab = useTabsStore.getState().schemaTabs.find(
          (t) => String(t.id) === String(useTabsStore.getState().activeSchemaTabId)
        );
        if (schemaTab?.schemaId != null) {
          ctx.duplicateSchemaEntry?.('schema', schemaTab.schemaId);
          return;
        }
      }

      if (useTabsStore.getState().activeTabType === 'request') {
        const activeTab = useTabsStore.getState().requestTabs.find(
          (t) => String(t.id) === String(useTabsStore.getState().activeRequestTabId)
        );
        const requestId = activeTab?.originalId ?? null;
        if (requestId) {
          const req = (ctx.collections || [])
            .flatMap((c) => c.requests || [])
            .find((r) => String(r.id) === String(requestId));
          if (req && req.can_edit !== false) {
            ctx.duplicateRequest?.(req);
          }
        }
      }
    };

    document.addEventListener('keydown', handleDuplicateShortcut, true);
    return () => document.removeEventListener('keydown', handleDuplicateShortcut, true);
  }, [keyboardShortcuts]);

  return { handleSidebarDuplicate };
}
